import React from "react";
import { Stack, Typography, Tooltip } from "@mui/material";
import type { ChatSource } from "@services/chat/types";

interface ChatSourcesProps {
  sources: ChatSource[];
  onSourceClick: (source: ChatSource) => void;
}

const getSourceEmoji = (type: string): string => {
  switch (type) {
    case "email":
      return "\u{1F4E7}";
    case "attachment":
      return "\u{1F4C4}";
    case "document":
      return "\u{1F4C4}";
    case "link":
      return "\u{1F517}";
    default:
      return "\u{1F4C4}";
  }
};

const getSourceLabel = (source: ChatSource): string => {
  if (source.type === "email") {
    return source.data.subject || "Email";
  }
  if (source.type === "attachment") {
    return source.data.filename || "Attachment";
  }
  return "Source";
};

const truncateText = (text: string, maxLength: number = 40): string => {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + "...";
};

const ChatSources: React.FC<ChatSourcesProps> = ({
  sources,
  onSourceClick,
}) => {
  if (!sources || sources.length === 0) {
    return null;
  }

  return (
    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
      {sources.map((source, index) => {
        const label = getSourceLabel(source);

        return (
          <Tooltip
            key={index}
            title={label}
            arrow
            placement="top"
          >
            <Typography
              variant="caption"
              onClick={() => onSourceClick(source)}
              sx={{
                fontFamily: "monospace",
                fontWeight: "bold",
                color: "primary.main",
                cursor: "pointer",
                fontSize: "0.6rem",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              [{getSourceEmoji(source.type)} {truncateText(label)}]
            </Typography>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

export default ChatSources;
